import { safe } from "@orpc/client";
import { IconAlertCircle } from "@tabler/icons-react";
import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import z from "zod";
import { useAppForm } from "@/components/form";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
	Field,
	FieldContent,
	FieldDescription,
	FieldGroup,
	FieldLabel,
	FieldTitle,
} from "@/components/ui/field";
import {
	FormCard,
	FormCardContent,
	FormCardFooter,
	FormCardHeader,
	FormCardHeaderSubtitle,
	FormCardTitle,
} from "@/components/ui/form-card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Slider } from "@/components/ui/slider";
import {
	CHANNEL_AUDIO_BITRATES,
	CHANNEL_AUDIO_NORMALIZATIONS,
	DEFAULT_CHANNEL_AUDIO_BITRATE,
	DEFAULT_CHANNEL_AUDIO_FORMAT_ID,
	DEFAULT_CHANNEL_AUDIO_NORMALIZATION,
} from "@/constants";
import { AudioFormat } from "@/enums/audio-format";
import { cn } from "@/lib/utils";
import { orpc, validationErrors } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId/audio")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params, preload }) {
		if (preload) {
			context.queryClient.prefetchQuery(
				orpc.channels.show.queryOptions({
					input: {
						channelId: params.channelId,
					},
				}),
			);
		}
	},
});

function Page() {
	const params = Route.useParams();

	const { data: channel } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const updateMutation = useMutation(
		orpc.channels.updateAudio.mutationOptions({
			onSuccess: (_data, _payload, _result, context) => {
				context.client.invalidateQueries({ queryKey: orpc.channels.key() });
				toast.success("Channel audio updated successfully.");
			},
			onError: () => {
				toast.error("Failed to update this channel audio settings.");
			},
		}),
	);

	const form = useAppForm({
		defaultValues: {
			FormatID: channel.AudioConfig?.FormatID ?? DEFAULT_CHANNEL_AUDIO_FORMAT_ID,
			Bitrate: channel.AudioConfig?.Bitrate ?? DEFAULT_CHANNEL_AUDIO_BITRATE,
			Normalization: Number(
				channel.AudioConfig?.Normalization ?? DEFAULT_CHANNEL_AUDIO_NORMALIZATION,
			),
		},
		canSubmitWhenInvalid: true,
		validators: {
			onSubmitAsync: async ({ value }) => {
				const { error } = await safe(
					updateMutation.mutateAsync({
						...value,
						ID: channel.ID,
					}),
				);

				if (error) {
					return (
						validationErrors(error) ?? "An error occurred while updating the channel audio config."
					);
				}
			},
		},
	});

	return (
		<form.AppForm>
			<form
				className="relative"
				id={form.formId}
				onSubmit={(e) => {
					e.preventDefault();
					e.stopPropagation();
					form.handleSubmit();
				}}
			>
				<div className="grid grid-cols-12 gap-6 px-4 lg:px-6">
					{!channel.AudioConfig && (
						<Alert variant="destructive" className="col-span-full">
							<IconAlertCircle />
							<AlertTitle>No Config</AlertTitle>
							<AlertDescription>
								No audio configuration has been set for this data, the values you see are the
								default values. Click save to create a new configuration.
							</AlertDescription>
						</Alert>
					)}
					<FormCard>
						<FormCardHeader>
							<FormCardTitle>Codec</FormCardTitle>
							<FormCardHeaderSubtitle>Choose the lossy audio format.</FormCardHeaderSubtitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="FormatID"
									children={(field) => (
										<RadioGroup
											value={field.state.value}
											onValueChange={field.handleChange}
											onBlur={field.handleBlur}
											className="col-span-full"
										>
											<FieldLabel htmlFor="mp3-format">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>MP3</FieldTitle>
														<FieldDescription className="text-xs">
															MP3 is the most universally supported format that works on virtually
															all devices and podcast platforms, making it the safest choice for
															maximum reach.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem value={AudioFormat.MP3} id="mp3-format" />
												</Field>
											</FieldLabel>
											<FieldLabel htmlFor="aac+-format">
												<Field orientation="horizontal">
													<FieldContent>
														<FieldTitle>AAC+</FieldTitle>
														<FieldDescription className="text-xs">
															AAC+ offers better audio quality at the same file size compared to MP3
															and is widely supported by modern devices and platforms, but may have
															occasional compatibility issues with older players.
														</FieldDescription>
													</FieldContent>
													<RadioGroupItem value={AudioFormat["AAC+"]} id="aac+-format" />
												</Field>
											</FieldLabel>
										</RadioGroup>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
						<FormCardFooter>
							For most podcasters, MP3 remains the recommended choice due to its universal
							compatibility, while AAC+ is ideal if you prioritize slightly better audio quality and
							your audience uses modern devices.
						</FormCardFooter>
					</FormCard>
					<FormCard>
						<FormCardHeader>
							<FormCardTitle>Bitrate</FormCardTitle>
							<FormCardHeaderSubtitle>
								The number of bits per second that can be transmitted along a digital network.
							</FormCardHeaderSubtitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Bitrate"
									children={(field) => (
										<field.FormItem>
											<div className="mb-2 flex items-center justify-between">
												<span className="text-muted-foreground">Poor quality, smaller file</span>
												<span className="text-muted-foreground">Better quality, larger file</span>
											</div>
											<Slider
												className="**:data-[slot=slider-thumb]:size-6!"
												value={[CHANNEL_AUDIO_BITRATES.indexOf(field.state.value)]}
												min={0}
												max={CHANNEL_AUDIO_BITRATES.length - 1}
												onValueChange={(tick) => {
													field.setValue(CHANNEL_AUDIO_BITRATES[tick as number]);
												}}
											/>
											<span
												className="text-muted-foreground mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium"
												aria-hidden="true"
											>
												{CHANNEL_AUDIO_BITRATES.map((bitrate, index) => (
													<button
														key={bitrate}
														type="button"
														className={cn(
															"flex w-0 flex-col justify-center gap-2",
															index === 0
																? "items-start"
																: index === CHANNEL_AUDIO_BITRATES.length - 1
																	? "items-end"
																	: "items-center",
														)}
														onClick={() => field.setValue(bitrate)}
													>
														<span className={cn("bg-muted-foreground/70 h-1 w-px")} />
														<span
															className={cn(
																"whitespace-nowrap",
																index === 0
																	? "text-left"
																	: index === CHANNEL_AUDIO_BITRATES.length - 1
																		? "text-right"
																		: "text-center",
															)}
														>
															{bitrate} kbps
														</span>
													</button>
												))}
											</span>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
						<FormCardFooter>
							Bitrate controls the balance between audio quality and file size - higher bitrates
							produce better sound quality but create larger files that take longer to download and
							use more storage space. For most podcasts, 128 kbps offers an excellent balance with
							clear, professional audio quality and reasonable file sizes. Choose lower values
							(64-96 kbps) if you prioritize smaller files for faster downloads, or higher values
							(192-320 kbps) if your content includes music or sound effects where maximum audio
							quality is important.
						</FormCardFooter>
					</FormCard>
					<FormCard>
						<FormCardHeader>
							<FormCardTitle>Normalization</FormCardTitle>
							<FormCardHeaderSubtitle>Select the audio loudness.</FormCardHeaderSubtitle>
						</FormCardHeader>
						<FormCardContent>
							<FieldGroup>
								<form.AppField
									name="Normalization"
									children={(field) => (
										<field.FormItem>
											<div className="mb-2 flex items-center justify-between">
												<span className="text-muted-foreground">Quieter overall volume</span>
												<span className="text-muted-foreground">Louder overall volume</span>
											</div>
											<Slider
												className="**:data-[slot=slider-thumb]:size-6!"
												value={[field.state.value]}
												min={-10}
												max={2}
												onValueChange={(tick) => {
													field.setValue(tick as number);
												}}
											/>
											<span
												className="text-muted-foreground mt-3 flex w-full items-center justify-between gap-1 px-2.5 text-xs font-medium"
												aria-hidden="true"
											>
												{CHANNEL_AUDIO_NORMALIZATIONS.map((normalization, index) => (
													<button
														key={normalization}
														type="button"
														className={cn(
															"flex w-0 flex-col justify-center gap-2",
															index === 0
																? "items-start"
																: index === CHANNEL_AUDIO_NORMALIZATIONS.length - 1
																	? "items-end"
																	: "items-center",
														)}
														onClick={() => field.setValue(normalization)}
													>
														<span
															className={cn(
																"bg-muted-foreground/70 h-1 w-px",
																index % 2 !== 0 && "h-0.5",
															)}
														/>
														<span
															className={cn(
																"whitespace-nowrap",
																index % 2 !== 0 && "opacity-0",
																index === 0
																	? "text-left"
																	: index === CHANNEL_AUDIO_NORMALIZATIONS.length - 1
																		? "text-right"
																		: "text-center",
															)}
														>
															{normalization} dB
														</span>
													</button>
												))}
											</span>
											<field.FormError />
										</field.FormItem>
									)}
								/>
							</FieldGroup>
						</FormCardContent>
						<FormCardFooter>
							Normalization adjusts your recording's overall volume to a target level without
							changing the relative loudness between different parts. Quiet sections stay
							proportionally quiet, loud sections stay proportionally loud - only the baseline
							volume changes.
						</FormCardFooter>
					</FormCard>
					<form.FormSaveBar />
				</div>
				<form.FormBottomBar />
			</form>
		</form.AppForm>
	);
}
